import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { FaCheckCircle, FaExclamationTriangle, FaArrowLeft, FaEnvelope } from 'react-icons/fa';

const EmailVerification = () => {
    const [status, setStatus] = useState('verifying'); // verifying, success, error
    const [message, setMessage] = useState('');
    const { token, email } = useParams();
    const navigate = useNavigate();

    useEffect(() => {
        document.body.style.overflow = 'initial !important';

        // We no longer auto-verify since we're using POST
        // Just validate the token and email parameters
        if (!token || !email) {
            setStatus('error');
            setMessage('Ungültiger Verifizierungslink.');
        } else {
            setStatus('ready');
        }

        return () => {
            document.body.style.overflow = '';
        };
    }, [token, email]);

    const verifyEmail = async () => {
        try {
            setStatus('verifying');
            const response = await axios.post(`/verify-email/${token}`, { email });
            setStatus('success');
            setMessage(response.data.message || 'E-Mail-Adresse erfolgreich bestätigt.');

            // Redirect to login page after a short delay
            setTimeout(() => {
                navigate('/login');
            }, 5000);
        } catch (err) {
            setStatus('error');
            setMessage(err.response?.data?.message || 'Fehler bei der Bestätigung der E-Mail-Adresse.');
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
            <motion.div
                className="max-w-md w-full space-y-6 bg-white p-8 rounded-xl shadow-lg"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
            >
                <div className="flex flex-col items-start justify-start">
                    <img
                        src="/images/logo/leadersoft-client-plattform.svg"
                        alt="Bernie Logo"
                        className="h-8 mb-2"
                    />
                </div>

                <div className="text-center">
                    {status === 'ready' && (
                        <>
                            <FaEnvelope className="h-12 w-12 text-blue-500 mx-auto" />
                            <h2 className="mt-4 text-xl font-semibold text-gray-800">E-Mail-Adresse bestätigen</h2>
                            <p className="mt-2 text-gray-600">Bitte klicken Sie auf den Button, um Ihre E-Mail-Adresse zu bestätigen.</p>
                            <div className="mt-6">
                                <button
                                    onClick={verifyEmail}
                                    className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-800 transition-colors"
                                >
                                    E-Mail-Adresse bestätigen
                                </button>
                            </div>
                        </>
                    )}

                    {status === 'verifying' && (
                        <>
                            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500 mx-auto"></div>
                            <h2 className="mt-4 text-xl font-semibold text-gray-800">E-Mail-Adresse wird bestätigt...</h2>
                            <p className="mt-2 text-gray-600">Bitte warten Sie, während wir Ihre E-Mail-Adresse bestätigen.</p>
                            <p className="mt-4 text-sm text-gray-500">Falls es länger dauert, können Sie auch manuell bestätigen:</p>
                            <div className="mt-2">
                                <button
                                    onClick={verifyEmail}
                                    className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-800 transition-colors"
                                >
                                    Bestätigen
                                </button>
                            </div>
                        </>
                    )}

                    {status === 'success' && (
                        <>
                            <FaCheckCircle className="h-12 w-12 text-green-500 mx-auto" />
                            <h2 className="mt-4 text-xl font-semibold text-gray-800">E-Mail-Adresse bestätigt!</h2>
                            <p className="mt-2 text-gray-600">{message}</p>
                            <p className="mt-4 text-gray-500 text-sm">Sie werden in wenigen Sekunden zur Anmeldeseite weitergeleitet...</p>
                        </>
                    )}

                    {status === 'error' && (
                        <>
                            <FaExclamationTriangle className="h-12 w-12 text-red-500 mx-auto" />
                            <h2 className="mt-4 text-xl font-semibold text-gray-800">Bestätigung fehlgeschlagen</h2>
                            <p className="mt-2 text-gray-600">{message}</p>
                            <div className="mt-6">
                                <button
                                    onClick={verifyEmail}
                                    className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-800 transition-colors"
                                >
                                    Erneut versuchen
                                </button>
                            </div>
                        </>
                    )}
                </div>

                <div className="flex items-center justify-center mt-6">
                    <Link to="/login" className="flex items-center text-sm text-blue-600 hover:text-blue-500">
                        <FaArrowLeft className="mr-2" />
                        Zurück zum Login
                    </Link>
                </div>
            </motion.div>
        </div>
    );
};

export default EmailVerification;
